"use client";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/shadcn/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/shadcn/tooltip";
import { cn } from "@/lib/utils";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { ChevronDown } from "lucide-react";
import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";

const Tabs = TabsPrimitive.Root;

const MORE_WIDTH = 44;
const RAIL_PADDING = 8;

const TRIGGER_CLASSES =
  "inline-flex flex-1 cursor-pointer data-[state=active]:shadow-sm disabled:cursor-not-allowed items-center justify-center whitespace-nowrap rounded-md px-3 h-6 text-xs leading-none font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:text-foreground z-1";

export type TabsListTab = {
  value: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  tooltip?: React.ReactNode;
  data?: Partial<Record<`data-${string}`, string>>;
};

type TabsListOverflowProps =
  | {
      overflow: true;
      tabs: readonly (string | TabsListTab)[];
      activeTab: string;
      onSelect: (tab: string) => void;
      fill?: boolean;
      align?: "start" | "center";
    }
  | {
      overflow?: false;
      tabs?: never;
      activeTab?: never;
      onSelect?: never;
      fill?: never;
      align?: never;
    };

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  Omit<React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>, "onSelect"> &
    TabsListOverflowProps
>(
  (
    {
      className,
      overflow,
      tabs,
      activeTab,
      onSelect,
      fill = true,
      align = "start",
      children,
      ...props
    },
    ref
  ) => {
    const [indicatorStyle, setIndicatorStyle] = useState({
      left: 0,
      top: 0,
      width: 0,
      height: 0,
    });
    const [hasInitialized, setHasInitialized] = useState(false);
    const tabsListRef = useRef<HTMLDivElement | null>(null);
    const measureRowRef = useRef<HTMLDivElement | null>(null);

    const tabItems: TabsListTab[] = (tabs ?? []).map((tab) =>
      typeof tab === "string" ? { value: tab, label: tab } : tab
    );
    const [visibleCount, setVisibleCount] = useState(tabItems.length);
    const [naturalWidth, setNaturalWidth] = useState(0);

    const updateIndicator = React.useCallback(() => {
      if (!tabsListRef.current) return;

      const activeTab = tabsListRef.current.querySelector<HTMLElement>(
        '[data-state="active"]'
      );
      if (!activeTab) return;

      const activeRect = activeTab.getBoundingClientRect();
      const tabsRect = tabsListRef.current.getBoundingClientRect();

      requestAnimationFrame(() => {
        setIndicatorStyle({
          left: activeRect.left - tabsRect.left,
          top: activeRect.top - tabsRect.top,
          width: activeRect.width,
          height: activeRect.height,
        });
      });
    }, []);

    const measure = useCallback(() => {
      const wrapper = tabsListRef.current;
      if (!wrapper) return;

      const widths = [
        ...wrapper.querySelectorAll<HTMLElement>("[data-measure-tab]"),
      ].map((node) =>
        Math.ceil(node.getBoundingClientRect().width || node.offsetWidth)
      );
      if (!widths.length || widths.some((w) => w <= 0)) return;

      const available = wrapper.clientWidth - RAIL_PADDING;
      if (available <= 0) return;

      const total = widths.reduce((sum, w) => sum + w, 0);
      setNaturalWidth(total + RAIL_PADDING);
      if (total <= available) {
        setVisibleCount(widths.length);
        return;
      }

      let used = MORE_WIDTH;
      let fits = 0;
      for (const w of widths) {
        if (used + w > available) break;
        used += w;
        fits++;
      }
      setVisibleCount(Math.max(1, fits));
    }, []);

    const sync = useCallback(() => {
      updateIndicator();
      measure();
    }, [updateIndicator, measure]);

    useEffect(() => {
      measure();

      const timeoutId = setTimeout(() => {
        updateIndicator();
        setTimeout(() => setHasInitialized(true), 10);
      }, 0);

      window.addEventListener("resize", sync);
      const observer = new MutationObserver(updateIndicator);
      const resizeObserver =
        typeof ResizeObserver !== "undefined" ? new ResizeObserver(sync) : null;

      if (tabsListRef.current) {
        observer.observe(tabsListRef.current, {
          attributes: true,
          childList: true,
          subtree: true,
        });
        resizeObserver?.observe(tabsListRef.current);
      }
      if (measureRowRef.current) {
        resizeObserver?.observe(measureRowRef.current);
      }

      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener("resize", sync);
        observer.disconnect();
        resizeObserver?.disconnect();
      };
    }, [updateIndicator, measure, sync]);

    const onRail = new Set(
      tabItems.slice(0, visibleCount).map((tab) => tab.value)
    );

    if (activeTab && !onRail.has(activeTab)) {
      const last = [...onRail].pop();
      if (last) onRail.delete(last);
      onRail.add(activeTab);
    }

    const visible = tabItems.filter((tab) => onRail.has(tab.value));
    const hidden = tabItems.filter((tab) => !onRail.has(tab.value));

    return (
      <div
        ref={tabsListRef}
        style={
          overflow
            ? {
                width: fill ? undefined : naturalWidth || undefined,
                maxWidth: "100%",
              }
            : undefined
        }
        className={cn(
          "relative",
          overflow && "flex w-full min-w-0 items-center gap-1",
          overflow && align === "center" && "justify-center"
        )}
      >
        <TabsPrimitive.List
          ref={ref}
          className={cn(
            "relative inline-flex items-center justify-center rounded-lg bg-muted p-1 text-sm leading-none font-medium text-muted-foreground w-full select-none",
            overflow && (fill ? "min-w-0 flex-1" : "min-w-0 w-auto"),
            className
          )}
          {...props}
        >
          {overflow
            ? visible.map((tab) => {
                const trigger = (
                  <TabsTrigger
                    value={tab.value}
                    disabled={tab.disabled}
                    className={cn("gap-2", !fill && "flex-none")}
                    {...tab.data}
                  >
                    {tab.icon}
                    {tab.label}
                  </TabsTrigger>
                );

                return tab.disabled && tab.tooltip ? (
                  <Tooltip key={tab.value}>
                    <TooltipTrigger asChild>
                      <span className="inline-flex">{trigger}</span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      {tab.tooltip}
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <React.Fragment key={tab.value}>{trigger}</React.Fragment>
                );
              })
            : children}
        </TabsPrimitive.List>
        {overflow && hidden.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger
              aria-label={`${hidden.length} more tabs`}
              className="flex h-6 shrink-0 cursor-pointer items-center gap-0.5 rounded-md px-2 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              {hidden.length}
              <ChevronDown className="size-3.5" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {hidden.map((tab) => (
                <DropdownMenuItem
                  key={tab.value}
                  disabled={tab.disabled}
                  className="flex gap-2"
                  onSelect={() => onSelect?.(tab.value)}
                >
                  {tab.icon}
                  {tab.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {/* Duplicates every label, so it must come after the real rail: a
            text query that matches a tab would otherwise resolve to this
            invisible copy first. */}
        {overflow && (
          <div
            ref={measureRowRef}
            aria-hidden
            className="pointer-events-none invisible absolute top-0 left-0 flex"
          >
            {tabItems.map((tab) => (
              <span
                key={tab.value}
                data-measure-tab={tab.value}
                className={cn(TRIGGER_CLASSES, "flex-none gap-2")}
              >
                {tab.icon}
                {tab.label}
              </span>
            ))}
          </div>
        )}
        <div
          className={cn(
            "absolute rounded-md bg-background shadow-sm pointer-events-none",
            hasInitialized && "transition-all duration-300 ease-in-out",
            !hasInitialized && "opacity-0"
          )}
          style={indicatorStyle}
        />
      </div>
    );
  }
);
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(TRIGGER_CLASSES, className)}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn("focus-visible:outline-none", className)}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsContent, TabsList, TabsTrigger };
